import { useState, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faFileInvoice, faPrint, faXmark, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import PDFViewerModal from './PDFViewerModal';
import DocumentViewerModal from './DocumentViewerModal';
import '../styles/modal-shared.css';

function WRDetailsModal({ show, onHide, wrDetails }) {
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [documentViewerState, setDocumentViewerState] = useState({ show: false, url: '', title: '', document: null });

  const docsrvUrl = import.meta.env.VITE_DOCSRV || '';

  const grandTotal = useMemo(() => {
    if (!wrDetails?.rates?.length) return 0;
    return wrDetails.rates.reduce((sum, rate) => sum + (parseFloat(rate.Qty) || 0) * (parseFloat(rate.Rate) || 0), 0);
  }, [wrDetails?.rates]);

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getDocumentUrl = (filename) => {
    if (!filename) return '#';
    if (filename.startsWith('http://') || filename.startsWith('https://')) return filename;
    const clean = filename.replace(/^\/+/, '');
    const base = docsrvUrl.endsWith('/') ? docsrvUrl : `${docsrvUrl}/`;
    return `${base}${clean}`;
  };

  const handlePrintReceipt = async () => {
    if (!wrDetails) return;
    try {
      toast.loading('Generating warehouse receipt…');
      const response = await api.post(`/api/warehouse/receipts/${wrDetails.idwarehouse}/receipt`, {}, { responseType: 'blob' });
      const contentType = response.headers['content-type'];
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);
      toast.dismiss();
      toast.success('Warehouse receipt generated');
      setDocumentViewerState({
        show: true,
        url: blobUrl,
        title: `Warehouse Receipt ${wrDetails.bookingNum || wrDetails.idwarehouse || ''}`,
        document: { descr: `Warehouse Receipt ${wrDetails.bookingNum || ''}`, fileName: `WR_${wrDetails.idwarehouse}.pdf`, blobUrl, type: contentType, isReceipt: true },
      });
    } catch (err) {
      toast.dismiss();
      toast.error(`Failed to generate receipt: ${err.message || 'Unknown error'}`);
    }
  };

  const handleDocumentClick = (doc, e) => {
    e.preventDefault();
    if (doc.isReceipt) { handlePrintReceipt(); return; }
    const docData = { ...doc, descr: doc.Description || 'Document', fileName: doc.filename, blobUrl: getDocumentUrl(doc.filename) };
    setDocumentViewerState({ show: true, url: docData.blobUrl, title: docData.descr, document: docData });
  };

  if (!wrDetails) return null;

  const freight  = wrDetails.freight  || [];
  const rates    = wrDetails.rates    || [];
  const documents = wrDetails.documents || [];

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        backdrop="static"
        centered
        dialogClassName="detail-modal"
      >
        {/* ── HEADER ── */}
        <Modal.Header closeButton>
          <div className="modal-title-row">
            <div className="modal-header-icon">
              <FontAwesomeIcon icon={faFileInvoice} />
            </div>
            <div className="modal-header-text">
              <h5>Warehouse Receipt Details</h5>
              {wrDetails.bookingNum && (
                <p className="modal-subtitle">Booking #{wrDetails.bookingNum}</p>
              )}
            </div>
          </div>
        </Modal.Header>

        {/* ── BODY ── */}
        <Modal.Body>
          {/* Info grid */}
          <div className="detail-info-grid">
            <div className="detail-info-field">
              <div className="detail-info-label">Booking Number</div>
              <div className="detail-info-value">{wrDetails.bookingNum || <span className="detail-info-value--muted">—</span>}</div>
            </div>
            <div className="detail-info-field">
              <div className="detail-info-label">Shipper</div>
              <div className="detail-info-value">{wrDetails.shipper || <span className="detail-info-value--muted">—</span>}</div>
            </div>
            <div className="detail-info-field">
              <div className="detail-info-label">Date of Receipt</div>
              <div className="detail-info-value">{formatDate(wrDetails.recDate)}</div>
            </div>
            <div className="detail-info-field">
              <div className="detail-info-label">Date of Entry</div>
              <div className="detail-info-value">{formatDate(wrDetails.entryDate)}</div>
            </div>
            <div className="detail-info-field">
              <div className="detail-info-label">Consignee</div>
              <div className="detail-info-value">{wrDetails.consignee || <span className="detail-info-value--muted">—</span>}</div>
            </div>
            <div className="detail-info-field">
              <div className="detail-info-label">Destination</div>
              <div className="detail-info-value">{wrDetails.destination || <span className="detail-info-value--muted">—</span>}</div>
            </div>
            <div className="detail-info-field">
              <div className="detail-info-label">Carrier</div>
              <div className="detail-info-value">{wrDetails.carrier || <span className="detail-info-value--muted">—</span>}</div>
            </div>
            <div className="detail-info-field">
              <div className="detail-info-label">Pro Number</div>
              <div className="detail-info-value">{wrDetails.proNum || <span className="detail-info-value--muted">—</span>}</div>
            </div>
            {wrDetails.notes !== undefined && (
              <div className="detail-notes-field">
                <div className="detail-info-label" style={{ marginBottom: 6 }}>Notes</div>
                <div className="detail-notes-value">{wrDetails.notes || <span className="detail-info-value--muted">No notes</span>}</div>
              </div>
            )}
          </div>

          {/* Freight */}
          <div className="detail-section">
            <div className="detail-section-header">
              <span className="detail-section-title">Freight</span>
              {freight.length > 0 && (
                <span className="detail-section-total" style={{ color: '#64748b', fontSize: '0.75rem' }}>
                  {freight.length} item{freight.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className={`detail-section-body${freight.length > 6 ? ' detail-section-body--scroll' : ''}`}>
              <table className="detail-table">
                <thead>
                  <tr>
                    <th>FID</th>
                    <th>Customer Ref</th>
                    <th>MAWB</th>
                    <th>HAWB</th>
                    <th>Notes</th>
                    <th>Pcs</th>
                    <th>Wt</th>
                    <th>POD Name</th>
                    <th>POD Date</th>
                  </tr>
                </thead>
                <tbody>
                  {freight.length > 0 ? freight.map((item) => (
                    <tr key={item.FID}>
                      <td>{item.FID}</td>
                      <td>{item.CustomerRef || '—'}</td>
                      <td>{item.MAWB || '—'}</td>
                      <td>{item.HAWB || '—'}</td>
                      <td>{item.Notes || '—'}</td>
                      <td>{item.Pcs}</td>
                      <td>{item.Wt}</td>
                      <td>{item.PODName || '—'}</td>
                      <td>{formatDate(item.PODDate)}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan="9" className="detail-table-empty">No freight found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Rates + Documents side by side */}
          <div className="detail-two-col">
            {/* Rates */}
            <div className="detail-section" style={{ margin: 0 }}>
              <div className="detail-section-header">
                <span className="detail-section-title">
                  <FontAwesomeIcon icon={faDollarSign} style={{ marginRight: 5 }} />
                  Rates
                </span>
                {grandTotal > 0 && (
                  <span className="detail-section-total">${grandTotal.toFixed(2)} total</span>
                )}
              </div>
              <div className={`detail-section-body${rates.length > 5 ? ' detail-section-body--scroll' : ''}`}>
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rates.length > 0 ? rates.map((rate, i) => (
                      <tr key={i}>
                        <td>{rate.Description}</td>
                        <td>{rate.Qty}</td>
                        <td>${parseFloat(rate.Rate).toFixed(2)}</td>
                        <td>${parseFloat(rate.Total || (rate.Qty * rate.Rate)).toFixed(2)}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan="4" className="detail-table-empty">No rates found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Documents */}
            <div className="detail-section" style={{ margin: 0 }}>
              <div className="detail-section-header">
                <span className="detail-section-title">
                  <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: 5 }} />
                  Documents
                </span>
              </div>
              <div className="detail-section-body">
                <ul className="detail-doc-list">
                  {/* Generated receipt always first */}
                  <li
                    className="detail-doc-item"
                    onClick={(e) => handleDocumentClick({ isReceipt: true }, e)}
                  >
                    <FontAwesomeIcon icon={faPrint} className="doc-icon" />
                    Warehouse Receipt — {wrDetails.bookingNum || wrDetails.idwarehouse || 'WR'}
                  </li>
                  {documents.map((doc) => (
                    <li
                      key={doc.id}
                      className="detail-doc-item"
                      onClick={(e) => handleDocumentClick(doc, e)}
                    >
                      <FontAwesomeIcon icon={faFileAlt} className="doc-icon" />
                      {doc.Description}
                    </li>
                  ))}
                  {documents.length === 0 && (
                    <li className="detail-doc-item" style={{ color: '#94a3b8', cursor: 'default', fontStyle: 'italic' }}>
                      No additional documents
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </Modal.Body>

        {/* ── FOOTER ── */}
        <Modal.Footer>
          <button className="detail-btn detail-btn--secondary" onClick={onHide}>
            <FontAwesomeIcon icon={faXmark} />
            Close
          </button>
        </Modal.Footer>
      </Modal>

      <PDFViewerModal show={showPdfViewer} onHide={() => setShowPdfViewer(false)} wrDetails={wrDetails} />
      <DocumentViewerModal
        show={documentViewerState.show}
        onHide={() => setDocumentViewerState(s => ({ ...s, show: false }))}
        document={documentViewerState.document}
      />
    </>
  );
}

export default WRDetailsModal;
